import { useCallback, useId, useMemo, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { useElementWidth } from '@/components/charts/useElementWidth';

export interface TrendPoint {
  /** Stable identity (the report bucket). */
  key: string;
  /** Short axis label. */
  label: string;
  /** Full label for the tooltip and the data table. */
  fullLabel: string;
  /** Parsed value — geometry only, never displayed. */
  value: number;
  /** The exact figure as it arrived from the API. */
  display: string;
  /** Secondary detail, e.g. "12 orders". */
  meta?: string;
}

export interface TrendChartProps {
  points: readonly TrendPoint[];
  /** Subject of the accessible name, e.g. "Daily sales". */
  title: string;
  /** Column header for the value in the screen-reader table. */
  valueHeader: string;
  height?: number;
}

const PADDING = { top: 14, right: 10, bottom: 26, left: 52 };
const TICK_COUNT = 4;

/** Rounds a maximum up to a 1/2/5×10ⁿ step so gridlines land on readable numbers. */
function niceMax(max: number): number {
  if (max <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const scaled = max / magnitude;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return step * magnitude;
}

function compactTick(value: number): string {
  const absolute = Math.abs(value);
  if (absolute >= 1_000_000) return `${Math.round(value / 100_000) / 10}M`;
  if (absolute >= 1_000) return `${Math.round(value / 100) / 10}k`;
  return String(Math.round(value * 100) / 100);
}

/**
 * Area + line trend, drawn as inline SVG at measured pixel width. Sighted users
 * get a hover/arrow-key tooltip; assistive tech gets the same numbers as a
 * visually-hidden table, with the exact money strings rather than the parsed
 * floats used for geometry.
 */
/**
 * Brand navy for the line, a lighter navy for the fill and hover marker.
 *
 * These were #4f46e5 / #6366f1 — indigo from a theme two changes back. They
 * survived both the green and the navy retheme because raw hex in an SVG never
 * goes through a token, so nothing pointed at them.
 */
const LINE_COLOR = '#0F2E50'; // brand-700
const AREA_COLOR = '#184577'; // brand-600

export function TrendChart({ points, title, valueHeader, height = 240 }: TrendChartProps) {
  const [containerRef, width] = useElementWidth<HTMLDivElement>();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const gradientId = useId();
  const liveId = useId();

  const plotWidth = Math.max(80, width - PADDING.left - PADDING.right);
  const plotHeight = Math.max(60, height - PADDING.top - PADDING.bottom);

  const geometry = useMemo(() => {
    const values = points.map((point) => point.value);
    const max = niceMax(Math.max(0, ...values));
    const stepX = points.length > 1 ? plotWidth / (points.length - 1) : 0;
    const coordinates = points.map((point, index) => ({
      x: PADDING.left + (points.length > 1 ? index * stepX : plotWidth / 2),
      y: PADDING.top + plotHeight - (point.value / max) * plotHeight,
    }));
    return { max, stepX, coordinates };
  }, [points, plotWidth, plotHeight]);

  const { max, coordinates } = geometry;
  const baseline = PADDING.top + plotHeight;

  const linePath = useMemo(() => {
    if (coordinates.length === 0) return '';
    if (coordinates.length === 1) {
      const only = coordinates[0];
      return `M ${PADDING.left} ${only.y} L ${PADDING.left + plotWidth} ${only.y}`;
    }
    return coordinates.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
  }, [coordinates, plotWidth]);

  const areaPath = useMemo(() => {
    if (coordinates.length === 0) return '';
    const start = coordinates.length === 1 ? PADDING.left : coordinates[0].x;
    const end = coordinates.length === 1 ? PADDING.left + plotWidth : coordinates[coordinates.length - 1].x;
    return `${linePath} L ${end} ${baseline} L ${start} ${baseline} Z`;
  }, [coordinates, linePath, plotWidth, baseline]);

  const labelStep = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(plotWidth / 78))));

  const pickIndex = useCallback(
    (clientX: number, bounds: DOMRect) => {
      if (points.length === 0) return null;
      const offset = clientX - bounds.left - PADDING.left;
      const ratio = plotWidth === 0 ? 0 : offset / plotWidth;
      const index = Math.round(ratio * Math.max(0, points.length - 1));
      return Math.min(points.length - 1, Math.max(0, index));
    },
    [points.length, plotWidth],
  );

  const handleMove = useCallback(
    (event: ReactMouseEvent<SVGSVGElement>) => {
      setActiveIndex(pickIndex(event.clientX, event.currentTarget.getBoundingClientRect()));
    },
    [pickIndex],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<SVGSVGElement>) => {
      if (points.length === 0) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        setActiveIndex((current) => {
          const next = (current ?? (direction > 0 ? -1 : points.length)) + direction;
          return Math.min(points.length - 1, Math.max(0, next));
        });
        return;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        setActiveIndex(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setActiveIndex(points.length - 1);
      } else if (event.key === 'Escape') {
        setActiveIndex(null);
      }
    },
    [points.length],
  );

  const active = activeIndex === null ? null : points[activeIndex];
  const activePoint = activeIndex === null ? null : coordinates[activeIndex];
  const tooltipLeft = activePoint
    ? Math.min(Math.max(activePoint.x, 70), Math.max(70, width - 70))
    : 0;

  return (
    <div className="relative" ref={containerRef}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${title} chart. ${points.length} data point${points.length === 1 ? '' : 's'}. Use the arrow keys to read each point; the full figures are also listed in the table below.`}
        aria-describedby={liveId}
        tabIndex={0}
        className="touch-pan-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
        onMouseMove={handleMove}
        onMouseLeave={() => setActiveIndex(null)}
        onBlur={() => setActiveIndex(null)}
        onKeyDown={handleKeyDown}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={AREA_COLOR} stopOpacity="0.28" />
            <stop offset="100%" stopColor={AREA_COLOR} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {Array.from({ length: TICK_COUNT + 1 }).map((_, index) => {
          const ratio = index / TICK_COUNT;
          const y = PADDING.top + plotHeight * ratio;
          return (
            <g key={index}>
              <line
                x1={PADDING.left}
                x2={PADDING.left + plotWidth}
                y1={y}
                y2={y}
                stroke="#e2e8f0"
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
              <text x={PADDING.left - 8} y={y + 3.5} textAnchor="end" className="fill-slate-400 text-[10px]">
                {compactTick(max * (1 - ratio))}
              </text>
            </g>
          );
        })}

        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path d={linePath} fill="none" stroke={LINE_COLOR} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

        {coordinates.map((point, index) => (
          <circle
            key={points[index].key}
            cx={point.x}
            cy={point.y}
            r={activeIndex === index ? 4.5 : points.length > 40 ? 0 : 2.5}
            fill="#ffffff"
            stroke={LINE_COLOR}
            strokeWidth={2}
          />
        ))}

        {activePoint ? (
          <line
            x1={activePoint.x}
            x2={activePoint.x}
            y1={PADDING.top}
            y2={baseline}
            stroke={AREA_COLOR}
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        ) : null}

        <line
          x1={PADDING.left}
          x2={PADDING.left + plotWidth}
          y1={baseline}
          y2={baseline}
          stroke="#cbd5e1"
          strokeWidth={1}
          shapeRendering="crispEdges"
        />

        {points.map((point, index) => {
          const isEdge = index === 0 || index === points.length - 1;
          if (!isEdge && index % labelStep !== 0) return null;
          const x = coordinates[index].x;
          const anchor = index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle';
          return (
            <text
              key={point.key}
              x={x}
              y={height - 8}
              textAnchor={points.length === 1 ? 'middle' : anchor}
              className="fill-slate-400 text-[10px]"
            >
              {point.label}
            </text>
          );
        })}
      </svg>

      {active ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1 -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg"
          style={{ left: `${tooltipLeft}px` }}
        >
          <p className="font-medium text-slate-900">{active.fullLabel}</p>
          <p className="mt-0.5 tabular-nums text-slate-600">{active.display}</p>
          {active.meta ? <p className="text-slate-400">{active.meta}</p> : null}
        </div>
      ) : null}

      <p className="sr-only" id={liveId} aria-live="polite">
        {active ? `${active.fullLabel}: ${active.display}${active.meta ? `, ${active.meta}` : ''}` : ''}
      </p>

      <table className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            <th scope="col">{valueHeader}</th>
            <th scope="col">Detail</th>
          </tr>
        </thead>
        <tbody>
          {points.map((point) => (
            <tr key={point.key}>
              <th scope="row">{point.fullLabel}</th>
              <td>{point.display}</td>
              <td>{point.meta ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
